import { useState } from 'react';
import { User, Key, Trash2, Copy, Check, Plus, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useApiTokens, useCreateApiToken, useDeleteApiToken, ApiToken } from '@/hooks/useApiTokens';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

type TabId = 'profile' | 'tokens';

const TABS: Array<{ id: TabId; label: string; icon: typeof User }> = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'tokens', label: 'API Tokens', icon: Key },
];

export function Settings() {
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between pb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-xtravirt-dark">Settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your profile and API access
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'tab',
              activeTab === tab.id && 'tab-active'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-6">
        {activeTab === 'profile' && <ProfileTab />}
        {activeTab === 'tokens' && <ApiTokensTab />}
      </div>
    </div>
  );
}

// ============ Profile Tab ============

function ProfileTab() {
  const { user } = useAuth();

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-xl">
      <div className="card">
        <div className="flex items-center gap-4 mb-6">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="h-16 w-16 rounded-full"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-xtravirt-cyan to-xtravirt-navy flex items-center justify-center">
              <User className="h-8 w-8 text-white" />
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold text-xtravirt-dark">{user.name}</h2>
            <p className="text-sm text-gray-600">{user.email}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={user.name}
              disabled
              className="input mt-1 w-full bg-gray-50"
            />
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user.email}
              disabled
              className="input mt-1 w-full bg-gray-50"
            />
          </div>

          <div>
            <label className="label">Access</label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'badge',
                  user.status === 'active'
                    ? 'badge-green'
                    : user.status === 'pending'
                      ? 'badge-yellow'
                      : 'badge-red'
                )}
              >
                {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
              </span>
              {user.isSuperAdmin && <span className="badge badge-purple">Super Admin</span>}
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-gray-500">
          Profile information is managed through your Google account.
        </p>
      </div>
    </div>
  );
}

// ============ API Tokens Tab ============

function ApiTokensTab() {
  const { data, isLoading } = useApiTokens();
  const createToken = useCreateApiToken();
  const deleteToken = useDeleteApiToken();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
      await deleteToken.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const tokens = data?.tokens || [];

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-xtravirt-dark">API Tokens</h3>
            <p className="text-sm text-gray-600">
              Create tokens to access the API programmatically
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Token
          </button>
        </div>
      </div>

      {/* New token display */}
      {newToken && (
        <div className="mb-6 rounded-lg border border-xtravirt-green/50 bg-xtravirt-green/10 p-4">
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-xtravirt-green mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-xtravirt-dark">Token Created</h4>
              <p className="text-sm text-gray-600 mb-2">
                Copy this token now. You won't be able to see it again.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white rounded px-3 py-2 text-sm font-mono text-gray-800 border">
                  {newToken}
                </code>
                <button
                  onClick={() => handleCopy(newToken, 'new')}
                  className="btn-secondary px-3"
                >
                  {copiedId === 'new' ? (
                    <Check className="h-4 w-4 text-xtravirt-green" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
              <button
                onClick={() => setNewToken(null)}
                className="mt-3 text-sm text-xtravirt-link hover:underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token list */}
      {tokens.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
          <Key className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900">No API tokens</h3>
          <p className="text-sm text-gray-500 mt-1">
            Create a token to access the API programmatically
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tokens.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              onDelete={() => handleDelete(token.id)}
              isDeleting={deleteToken.isPending}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTokenModal
          onClose={() => setShowCreateModal(false)}
          onCreate={async (name, expiresInDays) => {
            const result = await createToken.mutateAsync({ name, expiresInDays });
            setNewToken(result.token);
            setShowCreateModal(false);
          }}
          isCreating={createToken.isPending}
        />
      )}
    </div>
  );
}

interface TokenRowProps {
  token: ApiToken;
  onDelete: () => void;
  isDeleting: boolean;
}

function TokenRow({ token, onDelete, isDeleting }: TokenRowProps) {
  const isExpired = token.expiresAt && new Date(token.expiresAt) < new Date();

  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-white border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-gray-100 p-2">
          <Key className="h-5 w-5 text-gray-500" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-xtravirt-dark">{token.name}</span>
            {isExpired && (
              <span className="badge badge-red">Expired</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            <code className="bg-gray-100 px-1.5 py-0.5 rounded">{token.tokenPrefix}...</code>
            <span>Created {new Date(token.createdAt).toLocaleDateString()}</span>
            {token.lastUsedAt && (
              <span>Last used {new Date(token.lastUsedAt).toLocaleDateString()}</span>
            )}
            {token.expiresAt && !isExpired && (
              <span>Expires {new Date(token.expiresAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={onDelete}
        disabled={isDeleting}
        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        title="Revoke token"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

interface CreateTokenModalProps {
  onClose: () => void;
  onCreate: (name: string, expiresInDays?: number) => Promise<void>;
  isCreating: boolean;
}

function CreateTokenModal({ onClose, onCreate, isCreating }: CreateTokenModalProps) {
  const [name, setName] = useState('');
  const [expiration, setExpiration] = useState('never');

  const expirationOptions = [
    { value: 'never', label: 'Never' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '180', label: '180 days' },
    { value: '365', label: '1 year' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const expiresInDays = expiration === 'never' ? undefined : parseInt(expiration);
    await onCreate(name, expiresInDays);
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2 className="dialog-title">Create API Token</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Token Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CI/CD Pipeline"
              className="input mt-1 w-full"
              required
            />
          </div>

          <div>
            <label className="label">Expiration</label>
            <select
              value={expiration}
              onChange={(e) => setExpiration(e.target.value)}
              className="input mt-1 w-full"
            >
              {expirationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <p className="text-sm text-yellow-700">
              Make sure to copy your token after creation. You won't be able to see it again.
            </p>
          </div>
        </form>

        <div className="dialog-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={!name || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Token'}
          </button>
        </div>
      </div>
    </div>
  );
}
